import React, { useEffect, useState } from 'react';
import {
  Bot,
  Check,
  Globe2,
  Mail,
  Menu,
  Radar,
  Radio,
  X,
  Video,
} from 'lucide-react';

export function SpiderMark({ size = 34 }) {
  return (
    <img className="spider-mark" src="/favicon.svg" width={size} height={size} alt="" aria-hidden="true" />
  );
}

export function DiscordGlyph({ size = 18 }) {
  return (
    <svg className="discord-glyph" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M19.5 5.34A17.1 17.1 0 0 0 15.27 4l-.52 1.07a15.8 15.8 0 0 0-5.5 0L8.73 4A17.2 17.2 0 0 0 4.5 5.35C1.82 9.3 1.1 13.16 1.46 16.96A17.3 17.3 0 0 0 6.64 19.6l1.25-1.7a10.8 10.8 0 0 1-1.95-.94l.48-.37c3.76 1.73 7.84 1.73 11.55 0l.48.37c-.63.37-1.29.68-1.96.94l1.25 1.7a17.2 17.2 0 0 0 5.18-2.64c.42-4.4-.72-8.22-3.42-11.62ZM8.5 14.62c-1.13 0-2.06-1.04-2.06-2.32S7.35 9.98 8.5 9.98s2.08 1.05 2.06 2.32c0 1.28-.91 2.32-2.06 2.32Zm7 0c-1.13 0-2.06-1.04-2.06-2.32s.91-2.32 2.06-2.32 2.08 1.05 2.06 2.32c0 1.28-.91 2.32-2.06 2.32Z" />
    </svg>
  );
}

export function Brand({ compact = false }) {
  return (
    <a className={`brand ${compact ? 'compact' : ''}`} href="/" aria-label="Press Spider by Olamia AI, home">
      <span className="brand-mark"><SpiderMark size={compact ? 34 : 42} /></span>
      <span className="brand-copy"><strong>Press Spider</strong><small>Olamia AI</small></span>
    </a>
  );
}

export function Header({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <div className="shell header-inner">
        <Brand />
        <button className="icon-button mobile-menu" onClick={() => setOpen((value) => !value)} aria-label="Toggle navigation">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
        <nav className={open ? 'top-nav open' : 'top-nav'} aria-label="Main navigation">
          {!user && <><a href="/#how">How it works</a><a href="/#pricing">Pricing</a><a href="/#faq">FAQs</a></>}
          {user?.isAdmin && <a href="/admin">Admin</a>}
          {user ? (
            <>
              <a href="/app">Web</a>
              <button className="text-button" onClick={onLogout}>Log out</button>
            </>
          ) : <a className="button button-small" href="/app">Log in</a>}
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="shell footer-inner">
        <Brand compact />
        <p>Press Spider is an Olamia AI product, a brand of <a href="https://videogamemarketing.co" target="_blank" rel="noreferrer">Sherimedia sp. z o.o.</a></p>
        <nav aria-label="Footer navigation"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="mailto:hello@olamia.app">Contact</a></nav>
      </div>
    </footer>
  );
}

export function SourceGlyph({ source, size = 20 }) {
  if (source === 'youtube') return <Video size={size} />;
  if (source === 'twitch') return <Radio size={size} />;
  if (source === 'email') return <Mail size={size} />;
  if (source === 'discord') return <Bot size={size} />;
  if (source === 'press') return <Globe2 size={size} />;
  return <Radar size={size} />;
}

export function StatusPill({ children, tone = 'neutral' }) {
  return <span className={`status-pill ${tone}`}>{children}</span>;
}

export function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label className={`toggle ${disabled ? 'disabled' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange?.(event.target.checked)} disabled={disabled} aria-label={label} />
      <span aria-hidden="true"><i /></span>
    </label>
  );
}

export function CheckItem({ children }) {
  return <li><span><Check size={15} /></span>{children}</li>;
}

export function Modal({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="modal-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-head"><h2 id="modal-title">{title}</h2><button className="icon-button" onClick={onClose} aria-label="Close"><X size={20} /></button></div>
        {children}
      </section>
    </div>
  );
}

export function useToast() {
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(timer);
  }, [toast]);
  return {
    toast,
    showToast: (message, error = false) => setToast({ message, error }),
    Toast: () => toast ? <div className={`toast ${toast.error ? 'error' : ''}`} role="status">{toast.message}</div> : null,
  };
}

export function Spinner({ label = 'Loading' }) {
  return <div className="loading-row"><span className="spinner" aria-hidden="true" /><span>{label}</span></div>;
}
